import { Permission } from '@/@types/permission';

export type PermissionScope = {
  id: string;
  name: string | null;
  permissions: Permission[];
};

export type PermissionSection = {
  id: string;
  name: string;
  scopes: PermissionScope[];
};

const PERMISSION_ACTION_ORDER = [
  'read',
  'create',
  'update',
  'delete',
  'restore',
];

const PERMISSION_GROUP_ORDER = ['Geral'];

const getPermissionAction = (permissionName: string) => {
  return permissionName.split('_').pop() || permissionName;
};

const getPermissionActionOrder = (permissionName: string) => {
  const action = getPermissionAction(permissionName);
  const actionOrder = PERMISSION_ACTION_ORDER.indexOf(action);

  return actionOrder === -1 ? PERMISSION_ACTION_ORDER.length : actionOrder;
};

const sortPermissionsByAction = (permissions: Permission[]) => {
  return permissions
    .map((permission, index) => ({ index, permission }))
    .sort((firstPermission, secondPermission) => {
      const orderDifference =
        getPermissionActionOrder(firstPermission.permission.name) -
        getPermissionActionOrder(secondPermission.permission.name);

      if (orderDifference) {
        return orderDifference;
      }

      return firstPermission.index - secondPermission.index;
    })
    .map(({ permission }) => permission);
};

const getPermissionGroupOrder = (permissionSection: PermissionSection) => {
  const groupOrder = PERMISSION_GROUP_ORDER.indexOf(permissionSection.name);

  return groupOrder === -1 ? PERMISSION_GROUP_ORDER.length : groupOrder;
};

const sortPermissionSectionsByGroup = (sections: PermissionSection[]) => {
  return sections
    .map((section, index) => ({ index, section }))
    .sort((firstSection, secondSection) => {
      const orderDifference =
        getPermissionGroupOrder(firstSection.section) -
        getPermissionGroupOrder(secondSection.section);

      if (orderDifference) {
        return orderDifference;
      }

      return firstSection.index - secondSection.index;
    })
    .map(({ section }) => section);
};

export const getPermissionSections = (permissions: Permission[]) => {
  const sections = sortPermissionsByAction(permissions).reduce<
    PermissionSection[]
  >((currentSections, permission) => {
    const scopeName = permission.scope || null;
    const scopeId = scopeName || 'default';
    let section = currentSections.find(
      (currentSection) => currentSection.id === permission.group
    );

    if (!section) {
      section = {
        id: permission.group,
        name: permission.group,
        scopes: [],
      };
      currentSections.push(section);
    }

    let scope = section.scopes.find(
      (currentScope) => currentScope.id === scopeId
    );

    if (!scope) {
      scope = {
        id: scopeId,
        name: scopeName,
        permissions: [],
      };
      section.scopes.push(scope);
    }

    scope.permissions.push(permission);

    return currentSections;
  }, []);

  return sortPermissionSectionsByGroup(sections);
};
